import { HttpMethod } from '@teqplay/api'

import { requestBuilder } from '../builder'
import { ISODateTimeString, SHARE_MEDIUM, TRecipient } from './vesselJourney'

// Re-export for convenience
export type { ISODateTimeString, TRecipient }
export { SHARE_MEDIUM }

export type LineUpVesselStatus = 'APPROACHING' | 'AT_BERTH' | 'DEPARTED'

export type TLineUpVessel = {
  imo: string
  userEta?: ISODateTimeString
  readOnly?: {
    vesselName?: string
    status: LineUpVesselStatus
    ata?: ISODateTimeString
    atd?: ISODateTimeString
    addedAt: ISODateTimeString
  }
}

export type TLineUpCreate = {
  name: string
  berthId?: string
  vessels: TLineUpVessel[]
}

export type TLineUpUpdate = {
  name: string
  berthId?: string
  vessels: TLineUpVessel[]
}

export type TLineUp = {
  _id: string
  name: string
  berthId?: string
  readOnly?: {
    berthName?: string
    portId?: string
    portUnlocode?: string
  }
  vessels: TLineUpVessel[]
  sharedWith: TRecipient[]
  createdAt: ISODateTimeString
  createdBy: string
  updatedAt: ISODateTimeString
  updatedBy: string
  isGuest?: boolean
}

// GET /v1/lineup - Get user's lineups
export type TGetLineupsInput = void
export type TGetLineupsOutput = { owned: TLineUp[]; guest: TLineUp[] }

const getAll = requestBuilder.type<TGetLineupsInput, TGetLineupsOutput>().request(() => ({
  path: `/v1/lineup`,
  method: HttpMethod.GET
}))

// GET /v1/lineup/{id} - Get specific lineup by ID
export type TGetLineupByIdInput = { id: string }
export type TGetLineupByIdOutput = TLineUp

const getById = requestBuilder
  .type<TGetLineupByIdInput, TGetLineupByIdOutput>()
  .request(({ input: { id } }) => ({
    path: `/v1/lineup/${encodeURIComponent(id)}`,
    method: HttpMethod.GET
  }))

// POST /v1/lineup - Create new lineup
export type TCreateLineupInput = TLineUpCreate
export type TCreateLineupOutput = TLineUp

const create = requestBuilder
  .type<TCreateLineupInput, TCreateLineupOutput>()
  .request(({ input }) => ({
    path: `/v1/lineup`,
    method: HttpMethod.POST,
    init: {
      body: JSON.stringify({
        ...input,
        sharedWith: []
      })
    }
  }))

// PUT /v1/lineup/{id} - Update existing lineup
export type TUpdateLineupInput = { id: string; lineup: TLineUpUpdate }
export type TUpdateLineupOutput = TLineUp

const update = requestBuilder
  .type<TUpdateLineupInput, TUpdateLineupOutput>()
  .request(({ input: { id, lineup } }) => ({
    path: `/v1/lineup/${encodeURIComponent(id)}`,
    method: HttpMethod.PUT,
    init: {
      body: JSON.stringify({
        ...lineup,
        sharedWith: []
      })
    }
  }))

// DELETE /v1/lineup/{id} - Delete lineup
export type TDeleteLineupInput = { id: string }
export type TDeleteLineupOutput = void

const del = requestBuilder
  .type<TDeleteLineupInput, TDeleteLineupOutput>()
  .request(({ input: { id } }) => ({
    path: `/v1/lineup/${encodeURIComponent(id)}`,
    method: HttpMethod.DELETE
  }))

// PUT /v1/lineup/{id}/shareWith - Update sharing recipients
export type TShareLineupInput = { id: string; recipients: TRecipient[] }
export type TShareLineupOutput = TLineUp

const shareWith = requestBuilder
  .type<TShareLineupInput, TShareLineupOutput>()
  .request(({ input: { id, recipients } }) => ({
    path: `/v1/lineup/${encodeURIComponent(id)}/shareWith`,
    method: HttpMethod.POST,
    init: {
      body: JSON.stringify(recipients)
    }
  }))

const lineup = {
  getAll,
  getById,
  create,
  update,
  delete: del,
  shareWith
}

export default lineup
